import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import PageSeo from '../components/PageSeo';
import { Helmet } from "react-helmet-async";

  
const getDifficulty = (title) => {
  const lower = title.toLowerCase();
  if (lower.includes('ladakh') || lower.includes('spiti')) return { level: 'Expert+', icon: '🔴', color: '#EF4444' };
  if (lower.includes('himalayan') || lower.includes('mountain')) return { level: 'Expert', icon: '🟠', color: '#F97316' };
  if (lower.includes('coastal') || lower.includes('highway')) return { level: 'Intermediate', icon: '🟡', color: '#FBBF24' };
  return { level: 'Beginner', icon: '🟢', color: '#4ADE80' };
};

const getWeather = (date) => {
  const weatherOptions = [
    { temp: 28, condition: '☀️ Sunny', safety: 'Safe' },
    { temp: 24, condition: '⛅ Partly Cloudy', safety: 'Safe' },
    { temp: 18, condition: '🌧️ Light Rain', safety: 'Caution' },
  ];
  return weatherOptions[Math.floor(Math.random() * weatherOptions.length)];
};

const isRideExpired = (dateStr) => {
  if (!dateStr || dateStr === 'TBD' || dateStr === 'Every Sunday') return false;
  
  try {
    const rideDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return rideDate < today;
  } catch (err) {
    return false;
  }
};

export default function Rides() {
  const { rides, joinRide, leaveRide, joinedRides, createRidePlan, updateRidePlan, deleteRidePlan, user } = useApp();
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showShare, setShowShare] = useState(null);
  const [editingRide, setEditingRide] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newRide, setNewRide] = useState({ title: '', from: '', to: '', date: '', bikeReq: 'All bikes', max: 10, distance: '', desc: '' });
  const [created, setCreated] = useState(false);

  // Check if user is ride creator
  const isRideCreator = (ride) => {
    return ride.created_by === user?.id || ride.organizer === user?.name;
  };

  const shareRide = (ride, platform) => {
    const rideLink = `${window.location.origin}/rides?rideId=${ride.id}`;
    const shareText = `🏍️ Join my bike ride: "${ride.title}" on ${ride.date}!\n📍 ${ride.from} → ${ride.to} (${ride.distance})\n🎯 Difficulty: ${getDifficulty(ride.title).level}\n✅ MotoHub India Rides`;
    
    if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + rideLink)}`, '_blank');
    } else if (platform === 'instagram') {
      navigator.clipboard.writeText(rideLink);
      alert('📸 Link copied! Share to Instagram:\n' + shareText + '\n\nPaste link in bio or DM');
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(rideLink);
      alert('✅ Ride link copied!\nShare with friends!');
    } else if (platform === 'email') {
      window.open(`mailto:?subject=🏍️ Join Bike Ride: ${ride.title}&body=${encodeURIComponent(shareText + '\n\n' + rideLink)}`);
    }
  };

  // Filter and search
  let filtered = rides.filter(r => {
    // Status filter
    if (filter === 'open' && r.status !== 'open') return false;
    if (filter === 'joined' && !joinedRides.includes(r.id)) return false;
    
    // Difficulty filter
    if (difficultyFilter !== 'all') {
      const diff = getDifficulty(r.title).level;
      if (difficultyFilter === 'beginner' && !['Beginner'].includes(diff)) return false;
      if (difficultyFilter === 'intermediate' && !['Intermediate'].includes(diff)) return false;
      if (difficultyFilter === 'expert' && !['Expert', 'Expert+'].includes(diff)) return false;
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!r.title.toLowerCase().includes(query) &&
          !r.from.toLowerCase().includes(query) &&
          !r.to.toLowerCase().includes(query) &&
          !r.desc.toLowerCase().includes(query)) return false;
    }
    
    return true;
  });

  // Sort
  if (sortBy === 'date') {
    filtered = filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
  } else if (sortBy === 'distance') {
    filtered = filtered.sort((a, b) => {
      const distA = parseInt(a.distance) || 0;
      const distB = parseInt(b.distance) || 0;
      return distB - distA;
    });
  } else if (sortBy === 'riders') {
    filtered = filtered.sort((a, b) => (b.joined / b.max) - (a.joined / a.max));
  }

  const normalizedCity = user?.city?.trim().toLowerCase() || '';

  const recommendedRide = useMemo(() => {
    const openRidePool = rides.filter((ride) => ride.status === 'open');
    if (!openRidePool.length || !normalizedCity) return null;

    const scored = openRidePool.map((ride) => {
      let score = 0;
      if ((ride.from || '').toLowerCase() === normalizedCity) score += 4;
      if ((ride.to || '').toLowerCase() === normalizedCity) score += 2;
      if ((ride.from || '').toLowerCase().includes(normalizedCity) || (ride.to || '').toLowerCase().includes(normalizedCity)) score += 1;
      if (ride.tags?.includes('Weekend')) score += 0.5;
      return { ride, score };
    });

    return scored.sort((a, b) => b.score - a.score)[0]?.ride || null;
  }, [rides, normalizedCity]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (editingRide) {
      const { error } = await updateRidePlan(editingRide.id, newRide);
      if (error) {
        alert('Failed to update ride: ' + error.message);
        return;
      }
    } else {
      await createRidePlan({
        ...newRide,
        max: Number(newRide.max) || 10,
        tags: ['Community Planned', 'Open Invite'],
      });
    }
    setCreated(true);
    setTimeout(() => {
      setShowCreate(false);
      setCreated(false);
      setEditingRide(null);
      setNewRide({ title: '', from: '', to: '', date: '', bikeReq: 'All bikes', max: 10, distance: '', desc: '' });
    }, 1800);
  };

  const handleEditRide = (ride) => {
    if (isRideCreator(ride)) {
      setEditingRide(ride);
      setNewRide({ 
        title: ride.title, 
        from: ride.from, 
        to: ride.to, 
        date: ride.date, 
        bikeReq: ride.bikeReq, 
        max: ride.max, 
        distance: ride.distance, 
        desc: ride.desc 
      });
      setShowCreate(true);
    }
  };

  const handleDeleteRide = async (ride) => {
    if (isRideCreator(ride)) {
      if (window.confirm(`Delete ride "${ride.title}"? This action cannot be undone.`)) {
        const { error } = await deleteRidePlan(ride.id);
        if (error) {
          alert('Failed to delete ride: ' + error.message);
          return;
        }
        alert('Ride deleted successfully.');
      }
    }
  };

  const handleDuplicateRide = (ride) => {
    setEditingRide(null);
    setNewRide({
      title: `${ride.title} (Copy)`,
      from: ride.from,
      to: ride.to,
      date: '',
      bikeReq: ride.bikeReq || 'All bikes',
      max: ride.max || 10,
      distance: ride.distance || '',
      desc: ride.desc || '',
    });
    setShowCreate(true);
  };

  return (
    <div style={{ padding: '32px 36px', maxWidth: 900 }}>
  <PageSeo
  title="Weekend Bike Rides Near You | Trending Rides & Riders in Your City | MotoHub India"
  description="Discover trending bike rides, weekend rides near your city, and connect with riders in Pune, Mumbai, Bangalore and across India. Join group rides or plan your own ride with MotoHub India."
  keywords={[
    "bike rides near me",
    "weekend bike rides near me",
    "bike rides near Pune",
    "bike rides near Mumbai",
    "riders near your city",
    "motorcycle rides India",
    "group rides India",
    "trending bike rides India",
    "bike ride community India",
    "weekend ride planning India",
    "find riders near me",
    "bike trip near my location",
    "biker groups India",
    "local bike rides India"
  ]}
  path="/rides"
  
/>
      <div className="fade-up" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 36, letterSpacing: 1 }}>RIDES</div>
         <p style={{ color: "#aaa", maxWidth: "700px", lineHeight: "1.6", marginBottom: "20px" }}>
  Discover trending bike rides, weekend rides near your city, and connect with riders across India.
  Find local riders in Pune, Mumbai, Delhi, Punjab, Chennai, Kerala, Bangalore and plan group rides or solo trips easily with MotoHub India.
</p>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>Find your next adventure</div>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ background: 'var(--brand)', color: '#fff', padding: '10px 20px', borderRadius: 'var(--radius)', fontSize: 14, fontWeight: 600 }}>+ Plan a Ride</button>
      </div>
<div style={{
  background: "#111",
  padding: "20px",
  borderRadius: "12px",
  border: "1px solid #222",
  marginBottom: "20px"
}}>
  <h2 style={{ margin: 0 }}>
    🚀 Bike Rides Near {user?.city || "Your City"}
  </h2>
  <p style={{ color: "#aaa", marginTop: "8px" }}>
    Find weekend rides, trending routes, and biker communities near you.
   Discover trending weekend rides, local biker groups, and connect with riders in {user?.city || "your city"}.
  </p>
</div>

      {/* Search Bar */}
      <div style={{ marginBottom: 20 }}>
        <input 
          type="text" 
          placeholder="🔍 Search rides by title, location, or description..." 
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: 'var(--radius)',
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            color: 'var(--text)',
            fontSize: 14,
            marginBottom: 16
          }}
        />
      </div>

      {/* Filter and Sort Controls */}
      <div className="fade-up-1" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Status Filter */}
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'open', 'joined'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
              background: filter === f ? 'var(--brand)' : 'var(--bg2)',
              color: filter === f ? '#fff' : 'var(--text2)',
              border: `1px solid ${filter === f ? 'var(--brand)' : 'var(--border)'}`,
            }}>{f === 'all' ? 'All' : f === 'open' ? 'Open' : 'My Rides'}</button>
          ))}
        </div>

        {/* Difficulty Filter */}
        <div>
          <select value={difficultyFilter} onChange={e => setDifficultyFilter(e.target.value)} style={{
            padding: '7px 12px', borderRadius: 'var(--radius)', fontSize: 12, fontWeight: 500,
            background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)'
          }}>
            <option value="all">All Levels</option>
            <option value="beginner">🟢 Beginner</option>
            <option value="intermediate">🟡 Intermediate</option>
            <option value="expert">🔴 Expert</option>
          </select>
        </div>

        {/* Sort */}
        <div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
            padding: '7px 12px', borderRadius: 'var(--radius)', fontSize: 12, fontWeight: 500,
            background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)'
          }}>
            <option value="date">📅 By Date</option>
            <option value="distance">📍 By Distance</option>
            <option value="riders">👥 Most Popular</option>
          </select>
        </div>

        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)', alignSelf: 'center' }}>{filtered.length} rides found</span>
      </div>

      {recommendedRide && (
        <div className="fade-up-1" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 }}>
            ⭐ Best Match For {user.city}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700 }}>{recommendedRide.title}</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>
                {recommendedRide.from} to {recommendedRide.to} • {recommendedRide.date} • {recommendedRide.joined}/{recommendedRide.max} riders
              </div>
            </div>
            <button
              onClick={() => joinRide(recommendedRide.id)}
              style={{
                padding: '9px 16px',
                borderRadius: 'var(--radius)',
                background: joinedRides.includes(recommendedRide.id) ? 'var(--bg4)' : 'var(--brand)',
                color: joinedRides.includes(recommendedRide.id) ? 'var(--text2)' : '#fff',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {joinedRides.includes(recommendedRide.id) ? 'Joined' : 'Join Suggested Ride'}
            </button>
            <button
              onClick={() => setShowShare(recommendedRide)}
              style={{
                padding: '9px 12px',
                borderRadius: 'var(--radius)',
                background: 'var(--bg3)',
                color: 'var(--text2)',
                fontSize: 13,
                fontWeight: 600,
                border: '1px solid var(--border)',
              }}
            >
              📤 Invite Friends
            </button>
          </div>
        </div>
      )}

      {/* Rides List */}
      <div className="fade-up-2" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text2)' }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No rides found</div>
            <div style={{ fontSize: 13 }}>Try adjusting your filters or plan a new ride!</div>
          </div>
        ) : (
          filtered.map(ride => {
            const isJoined = joinedRides.includes(ride.id);
            const isCreator = isRideCreator(ride);
            const pct = Math.round((ride.joined / ride.max) * 100);
            return (
              <div key={ride.id} style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)', padding: '18px 20px', 
                transition: 'border-color 0.2s, transform 0.15s',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setSelected(ride)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 16, fontWeight: 600 }}>{ride.title}</span>
                      {(() => {
                        const diff = getDifficulty(ride.title);
                        return <span style={{ fontSize: 11, background: `${diff.color}20`, color: diff.color, padding: '2px 8px', borderRadius: 4 }}>{diff.icon} {diff.level}</span>;
                      })()}
                      <span style={{ fontSize: 11, background: ride.status === 'open' ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)', color: ride.status === 'open' ? 'var(--success)' : 'var(--warning)', padding: '2px 8px', borderRadius: 4 }}>
                        {ride.status === 'open' ? 'Open' : 'Full'}
                      </span>
                      {isJoined && <span style={{ fontSize: 11, background: 'rgba(232,76,30,0.12)', color: 'var(--brand)', padding: '2px 8px', borderRadius: 4 }}>Joined</span>}
                    </div>
                    {(() => {
                      const weather = getWeather(ride.date);
                      return (
                        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8, display: 'flex', gap: 12 }}>
                          <span>{weather.condition} • {weather.temp}°C</span>
                          <span style={{ padding: '1px 6px', borderRadius: 3, background: weather.safety === 'Safe' ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)', color: weather.safety === 'Safe' ? 'var(--success)' : 'var(--warning)' }}>{weather.safety}</span>
                        </div>
                      );
                    })()}
                    <div style={{ fontSize: 13, color: 'var(--text2)', display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                      <span>📅 {ride.date}</span>
                      <span>📍 {ride.from} to {ride.to}</span>
                      <span>🛣️ {ride.distance}</span>
                      <span>🏍️ {ride.bikeReq}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>👤 Organized by {ride.organizer}</div>
                  </div>
                  <div style={{ flexShrink: 0, textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>{ride.joined}/{ride.max} riders</div>
                    <div style={{ width: 80, height: 4, background: 'var(--bg4)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: pct >= 90 ? 'var(--warning)' : 'var(--brand)', borderRadius: 2 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {!isRideExpired(ride.date) && (
                        <button onClick={e => { e.stopPropagation(); joinRide(ride.id); }} style={{
                          padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500,
                          background: isJoined ? 'var(--bg4)' : ride.status === 'open' ? 'var(--brand)' : 'var(--bg3)',
                          color: isJoined ? 'var(--text2)' : '#fff',
                          cursor: 'pointer'
                        }}>{isJoined ? 'Joined' : ride.status === 'open' ? 'Join' : 'WL'}</button>
                      )}
                      {isJoined && !isRideExpired(ride.date) && (
                        <button onClick={e => { e.stopPropagation(); leaveRide(ride.id); }} style={{
                          padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: 12, fontWeight: 500,
                          background: 'var(--bg4)', color: 'var(--text2)', border: '1px solid var(--border)',
                          cursor: 'pointer'
                        }}>Leave</button>
                      )}
                      {isRideExpired(ride.date) && (
                        <div style={{ padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: 12, fontWeight: 500, background: 'var(--bg4)', color: 'var(--text3)' }}>Ride Past</div>
                      )}
                      <button onClick={e => { e.stopPropagation(); setShowShare(ride); }} style={{
                        padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500,
                        background: 'var(--bg3)', color: 'var(--text2)',
                        border: '1px solid var(--border)', cursor: 'pointer'
                      }}>📤</button>
                      {isCreator && (
                        <>
                          <button onClick={e => { e.stopPropagation(); handleEditRide(ride); }} style={{
                            padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500,
                            background: 'rgba(59,130,246,0.2)', color: '#3B82F6',
                            border: '1px solid rgba(59,130,246,0.3)', cursor: 'pointer'
                          }}>✏️ Edit</button>
                          <button onClick={e => { e.stopPropagation(); handleDeleteRide(ride); }} style={{
                            padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500,
                            background: 'rgba(239,68,68,0.2)', color: '#EF4444',
                            border: '1px solid rgba(239,68,68,0.3)', cursor: 'pointer'
                          }}>🗑️ Delete</button>
                        </>
                      )}
                      <button onClick={e => { e.stopPropagation(); handleDuplicateRide(ride); }} style={{
                        padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500,
                        background: 'rgba(16,185,129,0.2)', color: '#10B981',
                        border: '1px solid rgba(16,185,129,0.3)', cursor: 'pointer'
                      }}>Copy</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Detailed View Modal */}
      {selected && (
        <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: 28, maxWidth: 550, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, letterSpacing: 0.5 }}>{selected.title}</div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', color: 'var(--text2)', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Difficulty badge */}
            {(() => {
              const diff = getDifficulty(selected.title);
              return <div style={{ fontSize: 13, color: diff.color, marginBottom: 12, fontWeight: 600 }}>{diff.icon} {diff.level} • {selected.distance}</div>;
            })()}

            {/* Route and Date */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 14, color: 'var(--text2)' }}>
              <div>📅 {selected.date}</div>
              <div>📍 {selected.from} → {selected.to}</div>
              <div>🏍️ {selected.bikeReq}</div>
            </div>

            {/* Organizer */}
            <div style={{ background: 'var(--bg3)', padding: 12, borderRadius: 'var(--radius)', marginBottom: 16, fontSize: 13 }}>
              <div style={{ color: 'var(--text3)', marginBottom: 4 }}>Organized by</div>
              <div style={{ color: 'var(--text)', fontWeight: 600 }}>{selected.organizer}</div>
            </div>

            {/* Description */}
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 16, background: 'var(--bg3)', padding: 12, borderRadius: 'var(--radius)' }}>
              <div style={{ color: 'var(--text3)', marginBottom: 6, fontSize: 12 }}>About this ride</div>
              {selected.desc}
            </div>

            {/* Riders info */}
            <div style={{ background: 'var(--bg3)', padding: 12, borderRadius: 'var(--radius)', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>👥 {selected.joined}/{selected.max} Riders Joined</div>
              <div style={{ width: '100%', height: 6, background: 'var(--bg4)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: `${Math.round((selected.joined / selected.max) * 100)}%`, height: '100%', background: 'var(--brand)', borderRadius: 2 }} />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 10 }}>
              {!isRideExpired(selected.date) && (
                <button onClick={() => { joinRide(selected.id); setSelected(null); }} style={{ flex: 1, padding: 12, borderRadius: 'var(--radius)', background: joinedRides.includes(selected.id) ? 'var(--bg4)' : 'var(--brand)', color: joinedRides.includes(selected.id) ? 'var(--text2)' : '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                  {joinedRides.includes(selected.id) ? '✓ Joined' : 'Join This Ride'}
                </button>
              )}
              <button onClick={() => { setShowShare(selected); setSelected(null); }} style={{ flex: 1, padding: 12, borderRadius: 'var(--radius)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 15, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer' }}>
                📤 Invite Friends
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShare && (
        <div onClick={() => setShowShare(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 101, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: 24, maxWidth: 380, width: '100%' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 20 }}>Invite Friends to Ride</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>Share "{showShare.title}" with your friends via:</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => { shareRide(showShare, 'whatsapp'); setShowShare(null); }} style={{ padding: 14, borderRadius: 'var(--radius)', background: 'rgba(37, 211, 102, 0.2)', color: '#25D366', fontWeight: 600, fontSize: 14, border: '1px solid rgba(37, 211, 102, 0.4)', cursor: 'pointer' }}>
                💬 WhatsApp
              </button>
              <button onClick={() => { shareRide(showShare, 'instagram'); setShowShare(null); }} style={{ padding: 14, borderRadius: 'var(--radius)', background: 'rgba(245, 119, 217, 0.2)', color: '#F577D9', fontWeight: 600, fontSize: 14, border: '1px solid rgba(245, 119, 217, 0.4)', cursor: 'pointer' }}>
                📸 Instagram
              </button>
              <button onClick={() => { shareRide(showShare, 'email'); setShowShare(null); }} style={{ padding: 14, borderRadius: 'var(--radius)', background: 'var(--bg3)', color: 'var(--text)', fontWeight: 600, fontSize: 14, border: '1px solid var(--border)', cursor: 'pointer' }}>
                📧 Email
              </button>
              <button onClick={() => { shareRide(showShare, 'copy'); setShowShare(null); }} style={{ padding: 14, borderRadius: 'var(--radius)', background: 'var(--brand)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                🔗 Copy Link
              </button>
            </div>
            <button onClick={() => setShowShare(null)} style={{ width: '100%', marginTop: 14, padding: 10, borderRadius: 'var(--radius)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 13, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {/* Create/Edit Ride Modal */}
      {showCreate && (
        <div onClick={() => { setShowCreate(false); setEditingRide(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: 28, maxWidth: 460, width: '100%' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 20 }}>{editingRide ? 'EDIT RIDE' : 'PLAN A RIDE'}</div>
            {created ? (
              <div style={{ textAlign: 'center', padding: '30px 0' }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--success)' }}>✓ {editingRide ? 'Ride updated!' : 'Ride created!'}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 6 }}>Your ride is live for the community and chat.</div>
              </div>
            ) : (
              <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input placeholder="Ride title *" value={newRide.title} onChange={e => setNewRide({ ...newRide, title: e.target.value })} required />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input placeholder="Starting point *" value={newRide.from} onChange={e => setNewRide({ ...newRide, from: e.target.value })} required />
                  <input placeholder="Destination *" value={newRide.to} onChange={e => setNewRide({ ...newRide, to: e.target.value })} required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input type="date" value={newRide.date} onChange={e => setNewRide({ ...newRide, date: e.target.value })} required />
                  <input placeholder="Distance (e.g. 180 km)" value={newRide.distance} onChange={e => setNewRide({ ...newRide, distance: e.target.value })} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <select value={newRide.bikeReq} onChange={e => setNewRide({ ...newRide, bikeReq: e.target.value })}>
                    <option>All bikes</option><option>150cc+</option><option>250cc+</option><option>350cc+</option><option>500cc+</option>
                  </select>
                  <input type="number" placeholder="Max riders" value={newRide.max} onChange={e => setNewRide({ ...newRide, max: e.target.value })} min={2} max={50} />
                </div>
                <textarea placeholder="Ride description..." value={newRide.desc} onChange={e => setNewRide({ ...newRide, desc: e.target.value })} rows={3} style={{ resize: 'vertical' }} />
                <button type="submit" style={{ padding: 12, borderRadius: 'var(--radius)', background: 'var(--brand)', color: '#fff', fontSize: 15, fontWeight: 600, marginTop: 4, cursor: 'pointer' }}>{editingRide ? 'Update Ride' : 'Create Ride'}</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
